import { tileDataProp } from 'components/atoms/TileButton';
import ExpansionBar from 'components/molecules/ExpansionBar';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { defaultApplyFilter, salesDataType, salesTileItemType } from 'components/pages/GrowthAnalysis';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getGrowthAnalysisColumns } from 'utils/growthAnalysisTableColumns';
import { calculateContainerHeight, getConfig } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_CHANGE_CHART_COPY,
    PRICE_CHANGE_CHART_EXPORT,
    PRICE_CHANGE_TABLE_COPY,
    PRICE_CHANGE_TABLE_EXPORT,
} from 'utils/mixpanelEventNames';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { growthAnalysisDownload, growthAnalysisTableDownload } from 'parser/graphToExcelDownloadParser';
import TrendsChart from './TrendsChart';
import SalesChartContainerPriceChange from './SalesChartContainerPriceChange';
import dayjs from 'dayjs';
import { UserContext } from 'contexts/ConfigContext';

export const height = Math.max(250, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200
export const graphItemContainerClass = 'p-4 mb-4 border border-borderLinePurpule rounded';
export const subHeaderClass = 'm-0 font-medium font-lg text-subHeaderColor';
export const yAxisLabelClass = 'text-greyFont font-xs font-medium';

interface Props {
    salesTileData: tileDataProp[];
    salesTile: salesTileItemType;
    absTile: tileDataProp;
    volTile: tileDataProp;
    index: number;
    attributeTypeItem: any;
    attributeTypeItems: any[];
    drillDownHandler: (item: any, index: number, applyFilterObj: any) => void;
    dropdownItems: any[];
    dropdownClickHandler: (item: any, index: number, applyFilterObj: any) => void;
    attrTypeOptions: any;
    salesDataset: salesDataType[];
    drillDownSelectedData: any;
    previousApplyFilter: any;
    infiniteScrollerOnNext: (index: number, width: number) => void;
    infiniteScrollSettings: any;
    stringifiedFilter: string;
    secondStartRange: string;
    secondEndRange: string;
    selectedKpi: any;
    selectedTimePeriodDropdown: any;
    timePeriodSelection: { start_timestamp: number; end_timestamp: number };
    dateFormat: string;
    timeSelectionLabel: string;
    additionalDataCopy: Record<string, string[]>;
}

function formatLabel(label: string[], index: number, length: number) {
    //if there are 2 attribute types, it means comparison mode is enabled and hence change 2nd label only
    if (!label?.length || index + 1 < length) return '';
    return `- ${label.join(' > ')}`;
}

const graphExportHeaderMapping = {
    promo: ['Promo', 'Non-promo'],
    incremental: ['Base', 'Incremental'],
    sales: ['Sales'],
    depletion: ['Depletion'],
};

export const priceChangeAnalysisTableId = 'price-change-analysis-table';

const ExpandedViewPriceChange: FC<Props> = ({
    salesTileData,
    salesTile,
    absTile,
    volTile,
    index,
    attributeTypeItem,
    attributeTypeItems,
    drillDownHandler,
    dropdownItems,
    dropdownClickHandler,
    attrTypeOptions,
    salesDataset,
    drillDownSelectedData,
    infiniteScrollerOnNext,
    infiniteScrollSettings,
    stringifiedFilter,
    previousApplyFilter,
    secondEndRange,
    secondStartRange,
    selectedKpi,
    selectedTimePeriodDropdown,
    timePeriodSelection,
    dateFormat,
    timeSelectionLabel,
    additionalDataCopy,
}) => {
    const [expandedItem, setExpandedItem] = useState<any>(null);
    const [isTableExpanded, setIsTableExpanded] = useState(attributeTypeItems?.length < 2);
    const refSalesChartContainer = useRef(null);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const refTable = useRef(null);
    const headerLabels = useMemo(() => {
        const subHeaderLabel =
            salesTile.key === salesTileData[0].key
                ? `${selectedKpi?.label} change Chart By ${attributeTypeItem?.selectedAttributeType} ${formatLabel(
                      previousApplyFilter.labels,
                      index,
                      attributeTypeItems.length,
                  )}`
                : `${attributeTypeItem?.selectedAttributeType} - ${absTile.label} vs YA ${formatLabel(
                      previousApplyFilter.labels,
                      index,
                      attributeTypeItems.length,
                  )}`;
        const tableHeader = `${attributeTypeItem?.selectedAttributeType} ${absTile.label} vs YA Table ${formatLabel(
            previousApplyFilter.labels,
            index,
            attributeTypeItems.length,
        )}`;
        return { subHeaderLabel, tableHeader };
    }, [
        salesTile,
        absTile,
        selectedKpi,
        attributeTypeItem?.selectedAttributeType,
        salesDataset[0].chartData,
        salesDataset[1].chartData,
    ]);
    const globalTimePeriodSelectionLabel = useMemo(() => {
        // if (selectedTimePeriodDropdown?.value === 'custom') {
        //     const startDate = dayjs(secondStartRange, dateFormat).format(displayDateFormat);
        //     const endDate = dayjs(secondEndRange, dateFormat).format(displayDateFormat);
        //     return `[${startDate}-${endDate}]`;
        // }
        if (timePeriodSelection) {
            let startDate = dayjs.unix(timePeriodSelection.start_timestamp);
            let endDate = dayjs.unix(timePeriodSelection.end_timestamp);
            // if (timeSelectionLabel === 'Custom Date') {
            startDate = dayjs.unix(timePeriodSelection.start_timestamp).add(6, 'day').utc();
            endDate = dayjs.unix(timePeriodSelection.end_timestamp).add(6, 'day').utc();
            // }
            return `(${startDate.format(dateFormat)}-${endDate.format(dateFormat)})`;
        }
        return '';
    }, [timePeriodSelection]);

    const stringifiedGlobalFilter = useRef<string>(stringifiedFilter);

    //reset expanded item whenever global filter changes
    useEffect(() => {
        stringifiedGlobalFilter.current = stringifiedFilter;
        return () => {
            setExpandedItem(null);
        };
    }, [stringifiedFilter]);

    useEffect(() => {
        if (expandedItem) {
            prepareApplyFilterVariableAndExecute();
        }
    }, [expandedItem]);

    function prepareApplyFilterVariableAndExecute() {
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        if (expandedItem.dimension === 'dim1') {
            tempApplyFilterVariable.jsonstring_dim1.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        } else {
            tempApplyFilterVariable.jsonstring_dim2.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        }
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
    }

    //called when infinite scrolling
    const infiniteScrollerNext = (index: number, width: number = 0) => {
        infiniteScrollerOnNext(index, width);
    };

    //called when clicked on explore more icon on hovering sales graph
    const exploreMoreClickHandler = (item: any) => {
        const key = 'category';
        if (salesDataset[0].loading || salesDataset[1].loading) return;
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        if (_.isEqual(applyFilterItem, expandedItem)) setExpandedItem(null);
        else {
            setExpandedItem({ ...applyFilterItem });
        }
    };

    //called when right clicked on sales graph
    const rightClickHandler = (item: any, attrType: any, index: number) => {
        const key = 'category';
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        dropdownClickHandler(attrType, index, applyFilterItem);
    };

    //called when clicked on bar item on hovering sales graph
    const drillDownClickHandler = (item: any) => {
        const key = 'category';
        if (salesDataset[0].loading || salesDataset[1].loading) return;
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        // if (key in drillDownItem && item[0]?.[key] === drillDownItem?.[key]) setDrillDownItem({}); // undo/reset action
        // else setDrillDownItem(item[0]); //setting action
        drillDownHandler(
            { label: attributeTypeItem?.selectedAttributeType, value: attributeTypeItem?.selectedAttributeValue },
            attrTypeOptions.data?.length - dropdownItems?.length,
            applyFilterItem,
        );
    };

    //table columns
    const memoisedTableColumns = useMemo(() => {
        if (salesDataset[index].tableData?.length > 0) {
            return getGrowthAnalysisColumns(
                salesDataset[index].tableData,
                volTile,
                salesTile.key,
                expandedItem,
                drillDownSelectedData,
                commonConfig,
            );
        }
    }, [salesDataset, volTile, salesTile, expandedItem]);

    //header for table export
    const excelHeader = [
        attributeTypeItem?.selectedAttributeType,
        ...(salesDataset[index]?.tableData?.map((item: any) => {
            return item.type;
        }) || []),
    ];

    return (
        <div style={{ width: '100%' }}>
            <div className={` ${graphItemContainerClass}`} ref={refSalesChartContainer}>
                <div className="flex items-center gap-1 justify-between">
                    <p
                        className={subHeaderClass}
                    >{`${headerLabels.subHeaderLabel} - ${globalTimePeriodSelectionLabel}`}</p>
                    <span className="flex gap-2">
                        <CopyToClipBoard
                            mixpanelEvent={() => trackEventMixpanel(PRICE_CHANGE_CHART_COPY)}
                            refOfComponent={refSalesChartContainer}
                            extraData={additionalDataCopy}
                        />
                        {/* Header hardCoded for now */}
                        <DownloadExcel
                            mixpanelEvent={() => trackEventMixpanel(PRICE_CHANGE_CHART_EXPORT)}
                            title={priceChangeAnalysisTableId}
                            excelData={growthAnalysisDownload(salesDataset[index], salesTile)}
                            headers={[
                                attributeTypeItem?.selectedAttributeType,
                                ...graphExportHeaderMapping[salesTile.key],
                            ]}
                            titleArray={[
                                Object.entries(additionalDataCopy || {}).map(
                                    ([key, value]) => `${key}: ${value.join(', ')}`,
                                ),
                                [`Unit: ${volTile.label}`],
                            ]}
                        />
                    </span>
                </div>
                {/* SALES CHART AND INCREMENTAL/PROMO CHART */}
                <SalesChartContainerPriceChange
                    infiniteScrollSettings={infiniteScrollSettings}
                    infiniteScrollerNext={infiniteScrollerNext}
                    absTile={absTile}
                    volTile={volTile}
                    data={salesDataset[index]}
                    attributeTypeItem={attributeTypeItem}
                    attrTypeOptions={attrTypeOptions}
                    disableItemHover={attributeTypeItems?.length > 1 ? attributeTypeItems?.length - 1 === index : false}
                    disableDrillDownHover={attributeTypeItem?.disableHover}
                    disableRightClick={attributeTypeItems?.length > 1}
                    dropdownItems={dropdownItems}
                    drillDownOnClick={drillDownClickHandler}
                    index={index}
                    expandedItem={expandedItem}
                    drilledDownItem={drillDownSelectedData}
                    exploreMoreOnClick={exploreMoreClickHandler}
                    dropdownClickHandler={rightClickHandler}
                    isTableExpanded={isTableExpanded}
                />

                {!salesDataset[index].loading && !salesDataset[index].error && salesDataset[index].chartData?.data && (
                    <p className={`text-center !top-[-35px] mb-[-35px] relative ${yAxisLabelClass}`}>
                        {attributeTypeItem?.selectedAttributeType}
                    </p>
                )}
            </div>
            {expandedItem && (
                <div>
                    <TrendsChart
                        volTile={volTile}
                        index={index}
                        selectedTimePeriodDropdown={selectedTimePeriodDropdown}
                        expandedItem={expandedItem}
                        stringifiedFilter={stringifiedGlobalFilter.current}
                        secondStartRange={secondStartRange}
                        secondEndRange={secondEndRange}
                        selectedKpi={selectedKpi}
                        exportTitle={[
                            Object.entries(additionalDataCopy || {}).map(
                                ([key, value]) => `${key}: ${value.join(', ')}`,
                            ),
                            [`Unit: ${volTile.label}`],
                        ]}
                    />
                </div>
            )}

            <div
                id={`${priceChangeAnalysisTableId}-${index}`}
                className={`${index < attributeTypeItems?.length - 1 ? 'mb-4' : ''}`}
            >
                {attributeTypeItems?.length > 1 && (
                    <ExpansionBar
                        title={headerLabels.tableHeader}
                        isExpanded={isTableExpanded}
                        onClick={(visible: boolean) => setIsTableExpanded(visible)}
                    />
                )}
                {isTableExpanded && (
                    <>
                        <div className="flex justify-end mb-2 mt-4">
                            <span className="flex gap-2">
                                <CopyToClipBoard
                                    mixpanelEvent={() => trackEventMixpanel(PRICE_CHANGE_TABLE_COPY)}
                                    refOfComponent={refTable}
                                />

                                <DownloadExcel
                                    mixpanelEvent={() => trackEventMixpanel(PRICE_CHANGE_TABLE_EXPORT)}
                                    title={'priceChangeAnalysisTable'}
                                    excelData={growthAnalysisTableDownload(salesDataset[index].tableData, volTile)}
                                    headers={excelHeader}
                                    titleArray={[
                                        Object.entries(additionalDataCopy || {}).map(
                                            ([key, value]) => `${key}: ${value.join(', ')}`,
                                        ),
                                        [`Unit: ${volTile.label}`],
                                    ]}
                                />
                            </span>
                        </div>
                        <div ref={refTable}>
                            <GrowthAnalysisTable
                                tableLoading={salesDataset[index].loading}
                                columns={memoisedTableColumns}
                                data={salesDataset[index].tableData}
                                customHeight={350}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default React.memo(ExpandedViewPriceChange);
